// Session + station state. The operator identity mirrors what the BFF has
// already confirmed (the sf_op cookie is the source of truth server-side —
// this is only what the UI needs to render).

import type { StateCreator } from 'zustand';
import type { DeploymentInfo, OperatorVM } from '@/api/types';

export type AuthMethod = 'pin' | 'credentials';
export type AuthStage = 'chooser' | 'roster' | 'pin' | 'welcome' | 'credentials' | 'totp';
export type BootState = 'booting' | 'ready' | 'fatal';

export interface SessionSlice {
  boot: BootState;
  connMode: 'none' | 'kiosk' | 'user';
  authMethod: AuthMethod | null;
  authStage: AuthStage;
  operator: OperatorVM | null;
  /**
   * The Odoo user behind a credentials session, when there is one.
   *
   * Null on a kiosk: the device is authenticated as the service account, so
   * its uid says nothing about the operator behind the PIN. Todos, owned
   * andons and the leader home all key off this rather than off the employee.
   */
  userUid: number | null;
  /** Server-decided: whether this session sees the leader home. */
  isLeader: boolean;
  /** Where the Andon app lives, from the server's config. */
  andonAppUrl: string | null;
  /** quality.alert.team ids the leader's floor-wide quality list leaves out. */
  qaExcludedTeamIds: number[];
  /** The quality.alert.team an operator's alert opens with, from the server's config. */
  qaDefaultTeamId: number | null;
  /**
   * Whether this deployment has a standard-work library behind it.
   *
   * Decides only whether the More tab offers the row. It is not a permission:
   * every session that can sign in can read every ISW.
   */
  iswAvailable: boolean;
  /**
   * The andon module's three switches, as the server reported them for this
   * session — see `SessionInfo.capabilities.andon`.
   *
   * `preview` is the one the andon surfaces read: the raise flow, the thread,
   * push, the live stream, a leader's "Take it". The navigation — the five
   * tabs, the +, the Andon tab — is the same for every session; what the
   * switch changes is whether those places reach this app's own andon
   * screens or the Andon-app hand-off and the read-only sheet. All three
   * default to off, which is what a BFF without the module amounts to.
   */
  andonCaps: { module: boolean; sweep: boolean; preview: boolean };
  /** Selected on the roster, awaiting PIN confirmation. */
  pendingOperator: OperatorVM | null;
  /** Remembered for the "welcome back" fast path. */
  lastOperator: { id: number; name: string } | null;
  pin: string;
  loginError: string | null;
  /** A write returned operator_required — re-auth over the current screen. */
  reauthRequired: boolean;
  /** Clock offset (clientNow - serverNow) so elapsed time survives drift. */
  serverSkewMs: number;

  /**
   * Which Odoo this deployment talks to, from the server. Null until the
   * first session probe answers; the staging banner draws from it.
   */
  deployment: DeploymentInfo | null;

  // station
  workcentreId: number | null;
  workcentreName: string | null;
  flowlineId: number | null;
  flowlineName: string | null;

  setBoot: (b: BootState) => void;
  setConnMode: (m: 'none' | 'kiosk' | 'user') => void;
  setAuthMethod: (m: AuthMethod | null) => void;
  setAuthStage: (s: AuthStage) => void;
  setOperator: (o: OperatorVM | null) => void;
  setUser: (
    uid: number | null,
    isLeader: boolean,
    andonAppUrl: string | null,
    qaExcludedTeamIds?: number[],
    qaDefaultTeamId?: number | null,
  ) => void;
  setIswAvailable: (v: boolean) => void;
  /** What the server said about the andon module; undefined is everything off. */
  setAndonCapabilities: (caps: { module: boolean; sweep: boolean; preview: boolean } | undefined) => void;
  setPendingOperator: (o: OperatorVM | null) => void;
  pushPin: (digit: string) => void;
  popPin: () => void;
  clearPin: () => void;
  setLoginError: (e: string | null) => void;
  setReauthRequired: (v: boolean) => void;
  setServerSkew: (serverNow: number) => void;
  setDeployment: (d: DeploymentInfo | undefined) => void;
  setStation: (wc: { id: number; name: string } | null, fl: { id: number; name: string } | null) => void;
  resetOperator: () => void;
}

const LAST_OP_KEY = 'sf.lastOperator';
const STATION_KEY = 'sf.station';

function readLastOperator(): { id: number; name: string } | null {
  try {
    const raw = localStorage.getItem(LAST_OP_KEY);
    return raw ? (JSON.parse(raw) as { id: number; name: string }) : null;
  } catch {
    return null;
  }
}

export function readRememberedStation(): { workcentreId: number; flowlineId: number | null } | null {
  try {
    const raw = localStorage.getItem(STATION_KEY);
    return raw ? (JSON.parse(raw) as { workcentreId: number; flowlineId: number | null }) : null;
  } catch {
    return null;
  }
}

export const createSessionSlice: StateCreator<SessionSlice, [], [], SessionSlice> = (set, get) => ({
  boot: 'booting',
  connMode: 'none',
  authMethod: null,
  authStage: 'chooser',
  operator: null,
  userUid: null,
  isLeader: false,
  andonAppUrl: null,
  qaExcludedTeamIds: [],
  qaDefaultTeamId: null,
  iswAvailable: false,
  andonCaps: { module: false, sweep: false, preview: false },
  pendingOperator: null,
  lastOperator: readLastOperator(),
  pin: '',
  loginError: null,
  reauthRequired: false,
  serverSkewMs: 0,
  deployment: null,

  workcentreId: null,
  workcentreName: null,
  flowlineId: null,
  flowlineName: null,

  setBoot: (boot) => set({ boot }),
  setConnMode: (connMode) => set({ connMode }),
  setAuthMethod: (authMethod) => set({ authMethod }),
  setAuthStage: (authStage) => set({ authStage, pin: '', loginError: null }),
  setOperator: (operator) => {
    if (operator) {
      try {
        localStorage.setItem(LAST_OP_KEY, JSON.stringify({ id: operator.id, name: operator.name }));
      } catch {
        /* private mode — the fast path is a nicety, not a requirement */
      }
    }
    set({
      operator,
      pendingOperator: null,
      pin: '',
      loginError: null,
      reauthRequired: false,
      ...(operator ? { lastOperator: { id: operator.id, name: operator.name } } : {}),
    });
  },
  setUser: (userUid, isLeader, andonAppUrl, qaExcludedTeamIds = [], qaDefaultTeamId = null) =>
    set({ userUid, isLeader, andonAppUrl, qaExcludedTeamIds, qaDefaultTeamId }),
  setIswAvailable: (iswAvailable) => set({ iswAvailable }),
  // Each switch read on its own, so a server that sends two of the three —
  // or a stray truthy string — still lands on booleans. The preview is the
  // one that unlocks screens, and it must never be unlocked by accident.
  setAndonCapabilities: (caps) =>
    set({
      andonCaps: {
        module: caps?.module === true,
        sweep: caps?.sweep === true,
        preview: caps?.preview === true,
      },
    }),
  setPendingOperator: (pendingOperator) => set({ pendingOperator, pin: '', loginError: null }),
  pushPin: (digit) => {
    const { pin } = get();
    if (pin.length >= 4) return;
    set({ pin: pin + digit, loginError: null });
  },
  popPin: () => set((s) => ({ pin: s.pin.slice(0, -1), loginError: null })),
  clearPin: () => set({ pin: '' }),
  setLoginError: (loginError) => set({ loginError, pin: '' }),
  setReauthRequired: (reauthRequired) => set({ reauthRequired }),
  setServerSkew: (serverNow) => set({ serverSkewMs: Date.now() - serverNow }),
  // A server that says nothing is read as production, deliberately: the
  // banner exists to stop staging being mistaken for the real thing, and a
  // missing field must not put it on the real thing.
  setDeployment: (deployment) => set({ deployment: deployment ?? null }),
  setStation: (wc, fl) => {
    try {
      if (wc) {
        localStorage.setItem(
          STATION_KEY,
          JSON.stringify({ workcentreId: wc.id, flowlineId: fl?.id ?? null }),
        );
      }
    } catch {
      /* ignore */
    }
    set({
      workcentreId: wc?.id ?? null,
      workcentreName: wc?.name ?? null,
      flowlineId: fl?.id ?? null,
      flowlineName: fl?.name ?? null,
    });
  },
  resetOperator: () =>
    set({
      operator: null,
      pendingOperator: null,
      pin: '',
      reauthRequired: false,
      // The leader surface belongs to the person, not the device — signing out
      // must not leave the leader home switched on.
      userUid: null,
      isLeader: false,
    }),
});
